import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Search, Plus, Users, Eye, RefreshCw, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { WhatsAppButton } from './WhatsAppButton';
import { PossibleDuplicatesPanel } from './PossibleDuplicatesPanel';

const API = `${window.location.origin}/api`;

// Iter 66: Multi-column sort config. Keyed by header id; `kind` tells the
// sorter to compare numerically vs alphabetically vs chronologically.
const SORT_FIELDS = {
  name: { label: 'Name', kind: 'string' },
  jobs_done: { label: 'Jobs Done', kind: 'number' },
  last_job: { label: 'Last Job', kind: 'date' },
  ltv: { label: 'Lifetime Value', kind: 'number' },
};

const getJobsDone = (c) => {
  if (typeof c.completed_jobs_count === 'number') return c.completed_jobs_count;
  return (c.jobs || []).filter(j => j.status === 'completed').length;
};

const getLastJobTs = (c) => {
  const raw = c.last_invoice_paid_at || c.last_job_date;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
};

const getLtv = (c) => (typeof c.lifetime_value === 'number' ? c.lifetime_value : 0);

export const CustomersTab = ({
  customers,
  onAddCustomer,
  onViewCustomer,
  formatDate,
  authHeaders,
  onRefresh,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isBackfilling, setIsBackfilling] = useState(false);
  // Iter 66: Sort state — default "highest LTV first" which matches how Sandy
  // typically audits the customer database.
  const [sortKey, setSortKey] = useState('ltv');
  const [sortDir, setSortDir] = useState('desc');

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Numbers default to descending (most valuable first), text to ascending.
      setSortDir(SORT_FIELDS[key].kind === 'string' ? 'asc' : 'desc');
    }
  };

  const filteredCustomers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const base = !q
      ? customers
      : customers.filter(c =>
          c.name?.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q) ||
          c.address?.toLowerCase().includes(q) ||
          c.phone?.includes(searchTerm)
        );
    const getVal = (c) => {
      switch (sortKey) {
        case 'jobs_done': return getJobsDone(c);
        case 'last_job': return getLastJobTs(c);
        case 'ltv': return getLtv(c);
        case 'name':
        default: return (c.name || '').toLowerCase();
      }
    };
    const sorted = [...base].sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [customers, searchTerm, sortKey, sortDir]);

  const SortHeader = ({ fieldKey, children, className = '' }) => {
    const active = sortKey === fieldKey;
    const Icon = active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <TableHead className={className}>
        <button
          type="button"
          onClick={() => toggleSort(fieldKey)}
          className={`inline-flex items-center gap-1 font-medium hover:text-primary transition-colors ${active ? 'text-primary' : ''}`}
          data-testid={`customer-sort-${fieldKey}`}
          aria-label={`Sort by ${SORT_FIELDS[fieldKey].label} ${active && sortDir === 'asc' ? 'descending' : 'ascending'}`}
        >
          <span>{children}</span>
          <Icon className={`h-3.5 w-3.5 ${active ? 'opacity-100' : 'opacity-40'}`} />
        </button>
      </TableHead>
    );
  };

  const handleBackfillFromInvoices = async () => {
    setIsBackfilling(true);
    try {
      const res = await axios.post(`${API}/admin/customers/backfill-from-invoices`, {}, authHeaders);
      const { customers_updated, jobs_added, paid_invoices_scanned } = res.data || {};
      toast.success(
        `Synced ${customers_updated} customers · added ${jobs_added} job records (scanned ${paid_invoices_scanned} paid invoices)`,
      );
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Backfill failed');
    } finally {
      setIsBackfilling(false);
    }
  };

  return (
    <>
    <PossibleDuplicatesPanel authHeaders={authHeaders} onRefresh={onRefresh} />
    <Card>
      <CardHeader className="p-3 sm:p-6">
        <div className="flex flex-col gap-3">
          <CardTitle className="font-heading text-lg sm:text-xl">Customer Database</CardTitle>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-9"
                data-testid="customer-search"
              />
            </div>
            <Button
              onClick={onAddCustomer}
              className="bg-accent hover:bg-accent/90"
              data-testid="add-customer-btn"
            >
              <Plus className="h-4 w-4 mr-2" />Add Customer
            </Button>
            <Button
              onClick={handleBackfillFromInvoices}
              variant="outline"
              disabled={isBackfilling}
              data-testid="backfill-from-invoices-btn"
              title="Scan all paid invoices and add a completed job record to each customer so Jobs Done and Lifetime Value reflect the full picture"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isBackfilling ? 'animate-spin' : ''}`} />
              {isBackfilling ? 'Syncing…' : 'Sync from Invoices'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredCustomers.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No customers</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortHeader fieldKey="name">Name</SortHeader>
                <TableHead>Address</TableHead>
                <SortHeader fieldKey="jobs_done">Jobs Done</SortHeader>
                <SortHeader fieldKey="last_job">Last Job</SortHeader>
                <SortHeader fieldKey="ltv" className="text-right">Lifetime Value</SortHeader>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCustomers.map((customer) => {
                const plannedJobs = customer.jobs?.filter(j => j.status === 'planned').length || 0;
                const completedJobs = getJobsDone(customer);
                const ltv = getLtv(customer);
                return (
                  <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{customer.name}</p>
                        {customer.phone && (
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-sm text-muted-foreground">{customer.phone}</span>
                            <WhatsAppButton
                              phone={customer.phone}
                              name={customer.name}
                              service={customer.jobs?.[0]?.description}
                            />
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">{customer.address}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        <Badge variant="secondary" data-testid={`customer-jobs-done-${customer.id}`}>{completedJobs}</Badge>
                        {plannedJobs > 0 && <Badge className="bg-blue-100 text-blue-800">{plannedJobs} planned</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(customer.last_invoice_paid_at || customer.last_job_date)}</TableCell>
                    <TableCell className="text-right">
                      {ltv > 0 ? (
                        <span
                          className="font-medium text-emerald-700"
                          data-testid={`customer-ltv-${customer.id}`}
                        >
                          £{Number(ltv).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewCustomer(customer)}
                        data-testid={`view-customer-${customer.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
    </>
  );
};

export default CustomersTab;
